import {
  ChatMessage,
  Clause,
  ConditionalClause, DocumentFilter, FileDocument, IngestTextFragment, LabelAssignment, LabelIdentifier, SavedQuery,
  SelectionQuery, SelectionResult, StructuralClause,
} from '@/model';
import api from '@/rest-api';
import { RouteNames } from '@/router/route-names';
import realms from '@/store/realms';
import { distinct, groupBy, notEmpty } from '@/util/array-util';
import FragmentSelectionView from '@/views/FragmentSelectionView.vue';
import Vue, { VNode } from 'vue';

function latestExecutedQuery(): SelectionQuery | undefined {
  const latest = localStorage.getItem('latestQuery');
  return latest ? JSON.parse(latest) : undefined;
}

export default Vue.extend({
  name: 'FragmentSelectionController',
  components: {
    FragmentSelectionView,
  },

  props: {
    queryId: { type: Number as () => number | undefined, required: false },
    documentId: { type: Number as () => number | undefined, required: false },
  },

  data() {
    return {
      loading: false,
      loadingBoxIds: [] as number[],
      result: undefined as SelectionResult | undefined,
      queries: [] as SavedQuery[],
      documents: [] as FileDocument[],
      query: {} as Partial<SavedQuery>,
      realms: [realms.getters.main].filter(notEmpty) as number[] | undefined,
      assignments: undefined as Map<number, LabelAssignment[]> | undefined,
      identifiers: undefined as Promise<LabelIdentifier[]> | undefined,
      filter: undefined as DocumentFilter | undefined,
      chatHistory: [] as ChatMessage[],
      chatLoading: false as boolean,
    };
  },

  watch: {
    queryId: {
      immediate: true,
      async handler() {
        await this.load();
        if (this.queryId) {
          this.query = await api.getQuery(this.queryId);
        } else if (!this.documentId) {
          this.query = { id: 0, clause: latestExecutedQuery()?.clause || this.emptyClause() };
        }
      },
    },
    documentId: {
      immediate: true,
      async handler() {
        await this.load();
        if (this.documentId) {
          this.query = { clause: asSimpleClause([this.documentId]) };
          this.execute();
        }
      },
    },
  },

  computed: {
    selectionQuery(): SelectionQuery {
      return {
        clause: this.query?.clause || this.emptyClause(),
        page: undefined,
        realms: this.realms,
        documentFilter: this.filter,
      };
    },
  },

  methods: {
    emptyClause(): Clause {
      return {
        type: 'and',
        clauses: [],
      };
    },

    async reset() {
      if (this.queryId && this.queryId !== 0) {
        this.query = await api.getQuery(this.queryId);
      } else {
        this.query = { id: 0, clause: this.emptyClause() };
      }
    },

    async load() {
      this.loading = true;
      this.documents = await api.documentsFilter({ realms: this.realms });
      this.queries = await api.listQueries();
      this.loading = false;
    },

    async execute() {
      this.chatHistory = [];
      localStorage.setItem('latestQuery', JSON.stringify(this.selectionQuery));
      this.loading = true;
      if (this.selectionQuery) {
        this.result = await api.query(this.selectionQuery);
        const ids = distinct(this.result.docs
          .flatMap((d) => d.pages)
          .flatMap((p) => p.boxes)
          .flatMap((b) => b.slices)
          .flatMap((s) => s.fragments)).filter(notEmpty);

        const assignments = await api.fragmentLabelAssignments(ids);
        this.assignments = new Map();
        for (const a of assignments) {
          this.assignments.set(a.fragmentId, [...(this.assignments.get(a.fragmentId) || []), a.labelAssignment]);
        }
      }
      this.loading = false;
    },

    async executeChat(chat: string) {
      const userMessage: ChatMessage = { role: 'user', content: chat };
      const historyWithUser = [...this.chatHistory, userMessage];
      this.chatHistory = [...historyWithUser, { role: 'assistant', content: '' }];
      this.chatLoading = true;
      let hasTokens = false;
      try {
        await api.queryChatStream(
          this.selectionQuery,
          historyWithUser,
          (token: string) => {
            const lastIndex = this.chatHistory.length - 1;
            if (!hasTokens) {
              hasTokens = true;
              this.chatHistory = [...this.chatHistory.slice(0, lastIndex), { ...this.chatHistory[lastIndex], content: '' }];
            }
            const updated: ChatMessage = { ...this.chatHistory[lastIndex], content: this.chatHistory[lastIndex].content + token };
            this.chatHistory = [...this.chatHistory.slice(0, lastIndex), updated];
          },
          (status: string) => {
            if (!hasTokens) {
              const lastIndex = this.chatHistory.length - 1;
              this.chatHistory = [...this.chatHistory.slice(0, lastIndex), { ...this.chatHistory[lastIndex], content: status }];
            }
          },
        );
      } catch {
        const lastIndex = this.chatHistory.length - 1;
        this.chatHistory = [...this.chatHistory.slice(0, lastIndex), { role: 'assistant', content: 'The request timed out. Please try again.' }];
      } finally {
        this.chatLoading = false;
      }
    },

    async executeLabelIdentifiers() {
      this.identifiers = api.labelIdentifiers(this.selectionQuery);
    },

    changeQuery(query: SavedQuery) {
      if (query.id !== this.queryId) {
        this.$router.push({ name: RouteNames.SelectionQuery, params: { queryId: `${query.id}` } });
      }
    },
    changeFilter(filter: DocumentFilter) {
      this.filter = filter;
    },
    async saveQuery(query: SavedQuery) {
      this.loading = true;
      try {
        const saved = await api.saveQuery(query);
        this.changeQuery(saved);
      } finally {
        this.loading = false;
      }
    },

    updateQuery(query: SavedQuery) {
      this.query = query;
    },
    async changeRealms(rs: number[]) {
      this.realms = rs;
      await this.load();
    },

    async documentChanged(_: FileDocument) {
      await this.load();
      await this.execute();
    },
    async saveFragment(fragment: IngestTextFragment) {
      const boxIds = distinct(fragment.boxes.map((b) => b.boxId));
      try {
        this.loadingBoxIds = [...boxIds];
        await api.saveFragment(fragment);
        this.reloadBoxResult(boxIds);
      } finally {
        this.loadingBoxIds = [];
      }
    },
    fragmentsChanged(fragments: IngestTextFragment[]) {
      this.reloadBoxResult(
        distinct(
          fragments.flatMap((fragment) => [
            ...this.resultSlicesByFragmentId(fragment.id).map((s) => s.boxId),
            ...fragment.boxes.map((b) => b.boxId),
          ]),
        ),
      );
    },
    fragmentsDeleted(fragmentIds: number[]) {
      this.reloadBoxResult(
        distinct(
          fragmentIds.flatMap((fragmentId) => this.resultSlicesByFragmentId(fragmentId).map((s) => s.boxId)),
        ),
      );
    },
    resultSlicesByFragmentId(fragmentId: number) {
      return this.result?.docs
        .flatMap((d) => d.pages)
        .flatMap((p) => p.boxes)
        .flatMap((b) => b.slices)
        .filter((s) => s.fragments.includes(fragmentId))
        || [];
    },
    async reloadBoxResult(boxIds: number[]) {
      if (!this.query?.clause) {
        return;
      }
      try {
        this.loadingBoxIds = [...boxIds];
        const result = await api.query({
          clause: {
            type: 'and',
            clauses: [
              {
                type: 'in',
                field: 'box',
                values: boxIds,
              },
              this.query.clause,
            ],
          },
        });
        let updated: number[] = [];
        for (const doc of result.docs) {
          const existingDoc = this.result?.docs.find((d) => d.ingestId === doc.ingestId);
          if (existingDoc) {
            for (const page of doc.pages) {
              const existingPage = existingDoc.pages.find((p) => p.pageId === page.pageId);
              if (existingPage) {
                for (const box of page.boxes) {
                  const index = existingPage.boxes.findIndex((b) => b.boxId === box.boxId);
                  if (index > -1) {
                    updated = [...updated, box.boxId];
                    Vue.set(existingPage.boxes, index, box);
                  }
                }
              }
            }
          }
        }
        if (this.result) {
          for (const bid of boxIds) {
            if (!updated.includes(bid)) {
              for (const page of this.result.docs.flatMap((d) => d.pages)) {
                const missing = page.boxes.findIndex((b) => b.boxId === bid);
                Vue.delete(page.boxes, missing);
              }
            }
          }
        }
        const fragments = result.docs.flatMap((d) => d.pages).flatMap((p) => p.boxes).flatMap((b) => b.slices).flatMap((s) => s.fragments);
        const updatedAssignments = await api.fragmentLabelAssignments(fragments);
        if (!this.assignments) {
          this.assignments = new Map();
        }
        for (const [fragmentId, assignments] of groupBy(updatedAssignments, (a) => a.fragmentId)) {
          this.assignments.set(fragmentId, assignments.map((a) => a.labelAssignment));
        }
      } finally {
        this.loadingBoxIds = [];
      }
    },
  },

  render(create): VNode {
    return create(
      FragmentSelectionView, {
        props: {
          query: this.query,
          queries: this.queries,
          result: this.result,
          loading: this.loading,
          loadingBoxIds: this.loadingBoxIds,
          documents: this.documents,
          advanced: this.queryId && this.queryId > 0,
          realms: this.realms,
          assignments: this.assignments,
          identifiers: this.identifiers,
          filter: this.filter,
          chatHistory: this.chatHistory,
          chatLoading: this.chatLoading,
        },
        on: {
          execute: this.execute,
          executeChat: this.executeChat,
          loadIdentifiers: this.executeLabelIdentifiers,
          saveQuery: this.saveQuery,
          saveFragment: this.saveFragment,
          documentChanged: this.documentChanged,
          fragmentsChanged: this.fragmentsChanged,
          fragmentsDeleted: this.fragmentsDeleted,
          changeQuery: this.changeQuery,
          changeFilter: this.changeFilter,
          changeRealms: this.changeRealms,
          input: this.updateQuery,
          reset: this.reset,
        },
      },
    );
  },
});

export const asSimpleClause = (documents?: number[], labels?: number[], labelsExclude?: number[], anyLabel?: boolean): StructuralClause => {
  const clauses = [] as ConditionalClause[];
  if (documents && documents.length > 0) {
    clauses.push({
      type: 'in',
      field: 'document',
      values: documents,
    });
  }
  if (anyLabel) {
    if (labels && labels.length > 0) {
      clauses.push({
        type: 'in',
        field: 'label',
        values: labels,
      });
    }
  } else if (labels) {
    labels.forEach((label) => {
      clauses.push({
        type: 'in',
        field: 'label',
        values: [label],
      });
    });
  }
  if (labelsExclude && labelsExclude.length > 0) {
    clauses.push({
      type: 'in',
      field: 'label',
      negate: true,
      values: labelsExclude,
    });
  }
  return {
    type: 'and',
    clauses,
  };
};
